import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { generateExchangeInstIds, normalizeSymbolInput } from '@/lib/exchangeInstId';
import { categoryBadgeClass, hasCategory, renderCategoryLabel } from '@/lib/instrumentCategory';
import { getOppCommonSymbols, type OppCommonSymbolItem } from '@/services/oppDetectorApi';
import type { RealtimeSpreadDirection } from '@/services/oppDetectorApi';
import { SpreadTopList, oppSpreadItemToSelectorValue, toUiExchange } from '@/components/SpreadPairSelector/SpreadTopList';
import './SpreadPairSelector.css';
import './SpreadTopList.css';

export type SpreadPairSelectorValue = {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  direction?: RealtimeSpreadDirection;
};

type Props = {
  value: SpreadPairSelectorValue;
  exchanges: string[];
  disabled?: boolean;
  confirmDisabled?: boolean;
  useSymbolSearch?: boolean;
  showDirection?: boolean;
  category?: string | null;
  categoryLoaded?: boolean;
  /** Dropdown below trigger (default) or left-side drawer overlay. */
  panelLayout?: 'dropdown' | 'drawer';
  /** Show opp-detector spreads/top list inside drawer panel. */
  showSpreadTop?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onChange: (next: SpreadPairSelectorValue) => void;
  onConfirm?: (applied?: SpreadPairSelectorValue) => void;
};

const MAX_VISIBLE_OPTIONS = 80;

type DrawerPanelTab = 'search' | 'top100';

export function SpreadPairSelector({
  value,
  exchanges,
  disabled = false,
  confirmDisabled = false,
  useSymbolSearch = false,
  showDirection = false,
  category = null,
  categoryLoaded,
  panelLayout = 'dropdown',
  showSpreadTop = false,
  open: openProp,
  onOpenChange,
  onChange,
  onConfirm,
}: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [openInternal, setOpenInternal] = useState(false);
  const isControlledOpen = openProp != null;
  const open = isControlledOpen ? openProp : openInternal;

  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(open) : next;
    if (!isControlledOpen) setOpenInternal(resolved);
    onOpenChange?.(resolved);
  };
  const [symbolQuery, setSymbolQuery] = useState(value.symbol);
  const [drawerTab, setDrawerTab] = useState<DrawerPanelTab>('top100');
  const [symbols, setSymbols] = useState<OppCommonSymbolItem[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const exchangesReady =
    Boolean(value.exchange1) && Boolean(value.exchange2) && value.exchange1 !== value.exchange2;

  useEffect(() => {
    if (open) {
      setSymbolQuery('');
      setDrawerTab('top100');
    }
  }, [open]);

  useEffect(() => {
    if (!useSymbolSearch || !exchangesReady) {
      setSymbols([]);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoadingSymbols(true);
    setLoadError(null);
    void getOppCommonSymbols(value.exchange1, value.exchange2)
      .then((res) => {
        if (cancelled) return;
        setSymbols(res.symbols);
      })
      .catch((err) => {
        if (cancelled) return;
        setSymbols([]);
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingSymbols(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useSymbolSearch, exchangesReady, value.exchange1, value.exchange2]);

  const filteredSymbols = useMemo(() => {
    const needle = symbolQuery.trim().toUpperCase();
    if (!needle) return [];
    return symbols
      .filter((item) => item.symbol.toUpperCase().includes(needle))
      .slice(0, MAX_VISIBLE_OPTIONS);
  }, [symbolQuery, symbols]);

  useEffect(() => {
    if (panelLayout === 'drawer') return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [panelLayout]);

  useEffect(() => {
    if (!open || panelLayout !== 'drawer') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, panelLayout]);

  useEffect(() => {
    if (open && useSymbolSearch && exchangesReady && (panelLayout !== 'drawer' || drawerTab === 'search')) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [drawerTab, open, panelLayout, useSymbolSearch, exchangesReady]);

  const categoryLabel = hasCategory(category) ? renderCategoryLabel(category) : null;
  const showCategoryPending = categoryLoaded === false && value.symbol.trim().length > 0;

  const updateValue = (patch: Partial<SpreadPairSelectorValue>) => {
    onChange({ ...value, ...patch });
  };

  const pickSymbol = (item: OppCommonSymbolItem) => {
    setSymbolQuery(item.symbol);
    updateValue({
      symbol: item.symbol,
      instId1: item.inst_id1,
      instId2: item.inst_id2,
    });
  };

  const onSymbolDraftChange = (nextRaw: string) => {
    setSymbolQuery(nextRaw);
    const normalized = normalizeSymbolInput(nextRaw);
    const generated = generateExchangeInstIds(value.exchange1, value.exchange2, normalized || nextRaw);
    updateValue({
      symbol: nextRaw,
      instId1: generated.instId1,
      instId2: generated.instId2,
    });
  };

  const onExchangeChange = (field: 'exchange1' | 'exchange2', exchange: string) => {
    setSymbolQuery('');
    const normalizedSymbol = normalizeSymbolInput(value.symbol);
    if (field === 'exchange1') {
      updateValue({
        exchange1: exchange,
        instId1: generateExchangeInstIds(exchange, value.exchange2, normalizedSymbol).instId1,
      });
      return;
    }
    updateValue({
      exchange2: exchange,
      instId2: generateExchangeInstIds(value.exchange1, exchange, normalizedSymbol).instId2,
    });
  };

  const legSummary = `${value.exchange1}: ${value.instId1.trim() || '—'} / ${value.exchange2}: ${value.instId2.trim() || '—'}`;

  const pickSpreadTop = (item: Parameters<typeof oppSpreadItemToSelectorValue>[0]) => {
    const next = oppSpreadItemToSelectorValue(item);
    onChange(next);
    if (panelLayout === 'drawer') {
      onConfirm?.(next);
      setOpen(false);
    }
  };

  const searchPlaceholder = !exchangesReady
    ? '请先选择两个不同交易所'
    : useSymbolSearch
      ? loadingSymbols
        ? '加载 Symbol 列表…'
        : panelLayout === 'drawer'
          ? '搜索公共 Symbol'
          : '搜索 Symbol'
      : 'Symbol';

  const renderExchangeFields = () => (
    <div className="spread-pair-selector-exchanges">
      <label className="spread-pair-selector-field">
        <span>交易所 1</span>
        <select
          value={value.exchange1}
          disabled={disabled}
          onChange={(event) => onExchangeChange('exchange1', event.target.value)}
        >
          <option value={value.exchange1}>{value.exchange1}</option>
          {exchanges
            .filter((exchange) => exchange !== value.exchange1)
            .map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
        </select>
      </label>
      <label className="spread-pair-selector-field">
        <span>交易所 2</span>
        <select
          value={value.exchange2}
          disabled={disabled}
          onChange={(event) => onExchangeChange('exchange2', event.target.value)}
        >
          <option value={value.exchange2}>{value.exchange2}</option>
          {exchanges
            .filter((exchange) => exchange !== value.exchange2)
            .map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
        </select>
      </label>
    </div>
  );

  const renderSymbolList = (className = '') => (
    <ul id={listId} className={`spread-pair-selector-symbol-list${className ? ` ${className}` : ''}`} role="listbox">
      {loadError ? <li className="spread-pair-selector-hint is-error">{loadError}</li> : null}
      {!loadError && loadingSymbols ? <li className="spread-pair-selector-hint">加载中…</li> : null}
      {!loadError && !loadingSymbols && symbolQuery.trim().length === 0 ? (
        <li className="spread-pair-selector-hint">
          {symbols.length > 0 ? `输入 Symbol 搜索（共 ${symbols.length} 个公共标的）` : '输入 Symbol 搜索'}
        </li>
      ) : null}
      {!loadError && !loadingSymbols && symbolQuery.trim().length > 0 && filteredSymbols.length === 0 ? (
        <li className="spread-pair-selector-hint">无匹配 Symbol</li>
      ) : null}
      {!loadError
        ? filteredSymbols.map((item) => (
            <li key={item.symbol}>
              <button
                type="button"
                className={`spread-pair-selector-symbol-option${item.symbol === value.symbol ? ' is-selected' : ''}`}
                role="option"
                aria-selected={item.symbol === value.symbol}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickSymbol(item)}
              >
                <span className="spread-pair-selector-symbol-option-name">{item.symbol}</span>
                <span className="spread-pair-selector-symbol-option-meta">
                  {item.inst_id1} / {item.inst_id2}
                </span>
              </button>
            </li>
          ))
        : null}
    </ul>
  );

  const renderSearchInput = () => (
    <div className="spread-pair-selector-search-wrap">
      <span className="spread-pair-selector-search-icon" aria-hidden="true">
        ⌕
      </span>
      <input
        ref={searchRef}
        className="spread-pair-selector-search"
        value={symbolQuery}
        disabled={disabled || (useSymbolSearch && loadingSymbols)}
        placeholder={searchPlaceholder}
        aria-autocomplete={useSymbolSearch ? 'list' : undefined}
        aria-controls={useSymbolSearch ? listId : undefined}
        onChange={(event) => {
          if (useSymbolSearch) {
            setSymbolQuery(event.target.value);
          } else {
            onSymbolDraftChange(event.target.value);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (useSymbolSearch && event.key === 'Enter' && filteredSymbols.length > 0) {
            event.preventDefault();
            pickSymbol(filteredSymbols[0]);
          }
        }}
      />
    </div>
  );

  const renderInstFields = () => (
    <div className="spread-pair-selector-insts">
      <label className="spread-pair-selector-field">
        <span>Inst 1</span>
        <input
          value={value.instId1}
          disabled={disabled}
          placeholder={`${value.exchange1} 合约 ID`}
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => updateValue({ instId1: event.target.value })}
        />
      </label>
      <label className="spread-pair-selector-field">
        <span>Inst 2</span>
        <input
          value={value.instId2}
          disabled={disabled}
          placeholder={`${value.exchange2} 合约 ID`}
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => updateValue({ instId2: event.target.value })}
        />
      </label>
    </div>
  );

  const renderDirectionField = () =>
    showDirection ? (
      <label className="spread-pair-selector-field spread-pair-selector-direction">
        <span>方向</span>
        <select
          value={value.direction ?? 'long1_short2'}
          disabled={disabled}
          onChange={(event) => updateValue({ direction: event.target.value as RealtimeSpreadDirection })}
        >
          <option value="long1_short2">long1_short2</option>
          <option value="short1_long2">short1_long2</option>
        </select>
      </label>
    ) : null;

  const renderConfirmFooter = () =>
    onConfirm ? (
      <div className="spread-pair-selector-footer">
        <button
          type="button"
          className="spread-pair-selector-confirm"
          disabled={disabled || confirmDisabled}
          onClick={() => {
            onConfirm();
            setOpen(false);
          }}
        >
          确定
        </button>
      </div>
    ) : null;

  const renderSearchTabContent = () => {
    if (!exchangesReady) {
      return <p className="spread-pair-selector-hint">请选择两个不同的交易所后再搜索 Symbol。</p>;
    }

    return (
      <>
        {renderSearchInput()}
        {useSymbolSearch ? renderSymbolList(panelLayout === 'drawer' ? 'is-drawer' : '') : null}
        {renderInstFields()}
        {renderDirectionField()}
      </>
    );
  };

  const renderDrawerBody = (): ReactNode => (
    <>
      {renderExchangeFields()}
      <div className="spread-pair-selector-drawer-tabs" role="tablist" aria-label="筛选方式">
        <button
          type="button"
          role="tab"
          id="spread-pair-selector-tab-search"
          aria-selected={drawerTab === 'search'}
          aria-controls="spread-pair-selector-tabpanel-search"
          className={`spread-pair-selector-drawer-tab${drawerTab === 'search' ? ' is-active' : ''}`}
          onClick={() => setDrawerTab('search')}
        >
          Symbol 搜索
        </button>
        <button
          type="button"
          role="tab"
          id="spread-pair-selector-tab-top100"
          aria-selected={drawerTab === 'top100'}
          aria-controls="spread-pair-selector-tabpanel-top100"
          className={`spread-pair-selector-drawer-tab${drawerTab === 'top100' ? ' is-active' : ''}`}
          onClick={() => setDrawerTab('top100')}
        >
          Top 100
        </button>
      </div>
      <div className="spread-pair-selector-drawer-tab-panel">
        {drawerTab === 'search' ? (
          <div
            id="spread-pair-selector-tabpanel-search"
            role="tabpanel"
            aria-labelledby="spread-pair-selector-tab-search"
            className="spread-pair-selector-drawer-tab-content is-search"
          >
            {renderSearchTabContent()}
          </div>
        ) : (
          <div
            id="spread-pair-selector-tabpanel-top100"
            role="tabpanel"
            aria-labelledby="spread-pair-selector-tab-top100"
            className="spread-pair-selector-drawer-tab-content is-top100"
          >
            {!exchangesReady ? (
              <p className="spread-pair-selector-hint">请选择两个不同的交易所后再查看 Top 100。</p>
            ) : (
              <SpreadTopList
                exchange1={value.exchange1}
                exchange2={value.exchange2}
                enabled={open && drawerTab === 'top100'}
                selectedSymbol={value.symbol}
                selectedBuyExchange={toUiExchange(value.exchange1).toLowerCase()}
                selectedSellExchange={toUiExchange(value.exchange2).toLowerCase()}
                onPick={pickSpreadTop}
              />
            )}
          </div>
        )}
      </div>
      {renderConfirmFooter()}
    </>
  );

  const renderPanelBody = (): ReactNode => (
    <>
      {renderExchangeFields()}

      {!exchangesReady ? (
        <p className="spread-pair-selector-hint">请选择两个不同的交易所后再搜索 Symbol。</p>
      ) : (
        <>
          {renderSearchInput()}
          {useSymbolSearch && panelLayout !== 'drawer' ? renderSymbolList() : null}
        </>
      )}

      {renderInstFields()}
      {renderDirectionField()}
      {renderConfirmFooter()}
    </>
  );

  return (
    <div className={`spread-pair-selector${open ? ' is-open' : ''}${open && panelLayout === 'drawer' ? ' is-drawer-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="spread-pair-selector-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="spread-pair-selector-trigger-main">
          <span className="spread-pair-selector-trigger-title">
            <span className="spread-pair-selector-symbol">{value.symbol.trim() || '选择交易对'}</span>
            {categoryLabel ? (
              <span className={`spread-pair-selector-category ${categoryBadgeClass(category)}`}>
                {categoryLabel}
              </span>
            ) : showCategoryPending ? (
              <span className="spread-pair-selector-category is-pending">…</span>
            ) : null}
          </span>
          <span className="spread-pair-selector-trigger-meta">{legSummary}</span>
        </span>
        <span className="spread-pair-selector-chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && panelLayout === 'dropdown' ? (
        <div className="spread-pair-selector-panel" role="dialog" aria-label="交易对筛选">
          {renderPanelBody()}
        </div>
      ) : null}

      {open && panelLayout === 'drawer'
        ? createPortal(
            <>
              <button
                type="button"
                className="spread-pair-selector-drawer-backdrop"
                aria-label="关闭筛选抽屉"
                onClick={() => setOpen(false)}
              />
              <div className="spread-pair-selector-drawer" role="dialog" aria-label="交易对筛选与价差 Top">
                <header className="spread-pair-selector-drawer-head">
                  <div>
                    <h3 className="spread-pair-selector-drawer-title">筛选 & 价差机会</h3>
                    <p className="spread-pair-selector-drawer-desc">选择交易所后搜索公共 Symbol，或查看 Top 100 价差</p>
                  </div>
                  <button
                    type="button"
                    className="spread-pair-selector-drawer-close"
                    aria-label="关闭"
                    onClick={() => setOpen(false)}
                  >
                    ×
                  </button>
                </header>
                <div className="spread-pair-selector-drawer-body">
                  {showSpreadTop ? renderDrawerBody() : renderPanelBody()}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
